<script>
import cloneDeep from 'lodash/cloneDeep';
import { ConstManager } from '@goodt-wcore/managers';
import ProvideMixin from './ProvideMixin';
import { PropChangeContext } from './prop-change-context';
import { PanelUi } from '../components';
import { getDescriptorDefaultProps } from '../Elem';
import { createPanelStoreMeta, StoreOperation, resolvePropsDif, mergeProps } from '../Elem/infra';

/**
 * Panel events
 * @enum {string}
 */
const PanelEvent = Object.freeze({
    PROPS_CHANGE: 'props-change'
});

export { PanelEvent };

/**
 * @typedef {object} PanelMetaData
 * @property {string} name      panel name
 * @property {string} icon      mdi icon class
 */

/**
 * @type {import('./Panel').IPanelComponentOptionsInternal}
 */
export default {
    mixins: [ProvideMixin],
    components: { ...PanelUi },
    props: {
        /**
         * elem component instance reference
         * @public
         * @type {import('vue').PropOptions<import('vue')>}
         */
        // eslint-disable-next-line vue/no-unused-properties
        elementInstance: {
            type: Object
        },
        /**
         * elem instance props { key: value }
         * @type {import('vue').PropOptions<Record<string, any>>}
         */
        initProps: {
            type: Object,
            required: true
        },
        /** @type {import('vue').PropOptions<import('@goodt-wcore/core/types').ElemDescriptor>} */
        descriptor: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            /**
             * @public
             * @type {PanelMetaData} panel meta data (used by the editor env) */
            $meta: { name: '', icon: '' },
            /**
             * @public
             * @mutable elem instance props { key: value }
             */
            // eslint-disable-next-line goodt-rules/data-boolean-key-naming
            props: {}
        };
    },
    computed: {
        /**
         * @return {object}
         */
        propsDefault() {
            return getDescriptorDefaultProps(this.descriptor);
        },
        /**
         * @return {object}
         */
        propsMerged() {
            const { propsDefault, initProps } = this;
            return mergeProps(propsDefault, initProps);
        },
        /**
         * @public
         * @deprecated compatibility fix to be removed
         * @return [string[]]
         */
        envConstantsNames() {
            return Object.keys(ConstManager.instance.getConstantsHash());
        }
    },
    watch: {
        propsMerged: {
            handler(val) {
                this.props = cloneDeep(val);
            },
            deep: true,
            immediate: true
        }
    },
    beforeCreate() {
        this.StoreOperation = StoreOperation;
        this.$storeMeta = createPanelStoreMeta(this);
    },
    methods: {
        /**
         * Notifies env that the 'props' object has changed (used by the editor env)
         * @public
         */
        propChanged(propName, context) {
            const { propsDefault, props: propsNew } = this;
            const props = resolvePropsDif(propsDefault, propsNew);
            if (context == null) {
                context = new PropChangeContext({ prop: propName });
            }
            this.$emit(PanelEvent.PROPS_CHANGE, props, context);
        }
    }
};
</script>
